import React from 'react';
import { titleize } from 'underscore.string';
import { Glyphicon } from 'react-bootstrap';
import _ from 'underscore';
import classnames from 'classnames';
import CargomaticInput from 'components/v2/input';
import CargomaticSelect from 'components/v2/select';
import ToggleSwitch from 'components/v2/forms/ToggleSwitch';
import CustomerSearch from 'components/site/customer-search-alt';
import ErrorLabel from 'components/v2/forms/ErrorLabel';

import {
  assetTypes,
  equipmentTypes,
  equipmentSizes,
  equipmentTypesLocalizationSlugs,
  CHASSIS_OWNERSHIP_TYPE,
} from 'constants/Equipment';
import { useTranslation } from 'react-i18next';

const OrderEquipments = ({
  equipments,
  onAdd,
  onApplyAll,
  onChange,
  onRemoveEquipment,
}) => {
  if (!equipments || equipments.length === 0) return null;
  const { t } = useTranslation();
  const generateClassNames = (initialClasses, equipment, key) => {
    const classes = classnames(initialClasses, {
      border: equipment[key] === false,
      'border-red-400': equipment[key] === false,
      'border-solid': equipment[key] === false,
    });
    return classes;
  };

  return (
    <div className="p-3 text-sm bg-grey-lightest">
      <div className="flex mb-2">
        <div className="w-1/6 h-10">
          <span className="font-semibold text-grey-darker">
            {t('EQUIPMENT_ID')}
          </span>
        </div>
        <div className="w-1/6 h-10">
          <span className="font-semibold text-grey-darker mx-1.5">
            {t('EQUIPMENT_TYPE')}
          </span>
        </div>
        <div className="w-1/6 h-10">
          <span className="font-semibold text-grey-darker mx-1.5">
            {t('TYPE')}
          </span>
        </div>
        <div className="w-1/6 h-10">
          <span className="font-semibold text-grey-darker mx-1.5">
            {t('SIZE', { count: 1 })}
          </span>
        </div>
        <div className="w-1/12 h-10">
          <span className="font-semibold text-grey-darker mx-1.5">
            {t('PRIVATE')}
          </span>
        </div>
        <div className="w-1/6 h-10">
          <span className="font-semibold text-grey-darker mx-1.5">
            {t('CHASSIS_OWNERSHIP')}
          </span>
        </div>
        <div className="w-1/6 h-10">
          <span className="font-semibold text-grey-darker mx-1.5">
            {t('CUSTOMER')}
          </span>
        </div>
        <div className="w-1/5 h-10">&nbsp;</div>
      </div>
      {equipments.map((equipment) => {
        const equipmentTypeClasses = generateClassNames(
          'form-control h-full',
          equipment,
          '_isEquipmentTypeValid',
        );
        const typeClasses = generateClassNames(
          'form-control h-full',
          equipment,
          '_isTypeValid',
        );
        const equipmentSizeClasses = generateClassNames(
          'form-control h-full',
          equipment,
          '_isEquipmentSizeValid',
        );
        const chassisOwnerTypeClasses = generateClassNames(
          'form-control h-full',
          equipment,
          '_chassisOwnerType',
        );
        let equipmentIdFieldError = {};
        if (
          !equipment.equipment_id ||
          equipment.equipment_id.trim().length === 0
        ) {
          equipmentIdFieldError = { message: t('REQUIRED_FIELD') };
        }
        if (
          equipments.filter((e) => e.equipment_id === equipment.equipment_id)
            ?.length > 1
        ) {
          equipmentIdFieldError = { message: t('EQUIPMENT_ID_MUST_BE_UNIQUE') };
        }
        return (
          <div className="flex mb-5" key={equipment.equipment_uuid}>
            <div className="w-1/6 h-10">
              <CargomaticInput
                value={equipment.equipment_id}
                hideLabel
                error={equipmentIdFieldError}
                classNames="h-full"
                inputClassNames="form-field h-full  w-full"
                onChange={(value) => {
                  onChange(equipment, 'equipment_id', value);
                }}
                autoFocus
                name="equipment-id-input"
              />
            </div>
            <div className="w-1/6 h-10">
              <div className="flex h-full">
                <CargomaticSelect
                  selected={equipment._equipmentType}
                  className="mx-1.5  w-full"
                  inputClassNames={equipmentTypeClasses}
                  onSelect={(selectedObject) => {
                    onChange(equipment, '_equipmentType', selectedObject.value);
                  }}
                  error={
                    equipment._isEquipmentTypeValid === false
                      ? { message: t('REQUIRED_FIELD') }
                      : {}
                  }
                  hideLabel
                  defaultSelectLabel=""
                  items={assetTypes.map((assetType) => {
                    return {
                      localizationSlug: assetType?.toUpperCase(),
                      value: assetType,
                    };
                  })}
                  textKey="localizationSlug"
                  valueKey="value"
                  dataTestId="equipment-type-dropdown"
                />
              </div>
            </div>
            <div className="w-1/6 h-10">
              <div className="flex h-full">
                <CargomaticSelect
                  selected={equipment._type}
                  className="mx-1.5  w-full"
                  disabled={
                    equipment._equipmentType === 'chassis' ||
                    equipment._equipmentType === 'trailer'
                  }
                  error={
                    equipment._isTypeValid === false
                      ? { message: t('REQUIRED_FIELD') }
                      : {}
                  }
                  inputClassNames={typeClasses}
                  hideLabel
                  defaultSelectLabel=""
                  items={_.map(equipmentTypes, (label, key) => {
                    return {
                      localizationSlug: equipmentTypesLocalizationSlugs[key],
                      value: label,
                    };
                  })}
                  textKey="localizationSlug"
                  valueKey="value"
                  onSelect={(selectedObject) => {
                    onChange(equipment, '_type', selectedObject?.value);
                  }}
                  dataTestId="equipment-type-2-dropdown"
                />
              </div>
            </div>
            <div className="w-1/6 h-10">
              <div className="flex h-full">
                <CargomaticSelect
                  selected={equipment._equipmentSize}
                  className="mx-1.5  w-full"
                  disabled={equipment._equipmentType === 'chassis'}
                  inputClassNames={equipmentSizeClasses}
                  onSelect={(value) => {
                    onChange(equipment, '_equipmentSize', value);
                  }}
                  hideLabel
                  defaultSelectLabel=""
                  items={equipmentSizes}
                  error={
                    equipment._isEquipmentSizeValid === false
                      ? { message: t('REQUIRED_FIELD') }
                      : {}
                  }
                  dataTestId="equipment-size-dropdown"
                />
              </div>
            </div>
            <div className="w-1/12 h-10">
              <div className="flex h-full items-center mx-1.5">
                <ToggleSwitch
                  data-testid="equipment-is-private-switch"
                  disabled={equipment?._equipmentType !== 'chassis'}
                  enabled={!!equipment?.is_private}
                  onChange={(value) => {
                    onChange(equipment, 'is_private', value);
                  }}
                />
              </div>
            </div>
            <div className="w-1/6 h-10">
              <div className="flex h-full">
                <CargomaticSelect
                  selected={equipment?.chassis_owner_type}
                  className="mx-1.5 w-full"
                  disabled={!(!!equipment?.is_private)}
                  inputClassNames={chassisOwnerTypeClasses}
                  onSelect={({ value }) => {
                    onChange(equipment, 'chassis_owner_type', value);
                  }}
                  hideLabel
                  defaultSelectLabel=""
                  items={CHASSIS_OWNERSHIP_TYPE}
                  textKey="localizationSlug"
                  valueKey="value"
                  error={
                    equipment._chassisOwnerType === false
                      ? { message: t('REQUIRED_FIELD') }
                      : {}
                  }
                  data-testid="equipment-owner-type-dropdown"
                />
              </div>
            </div>
            <div className="w-1/6 h-10">
              <div className="flex h-full">
                <fieldset className={classnames({
                  'md:mr-2': true,
                  error: equipment?._chassisOwner === false,
                })}>
                  <CustomerSearch
                    id="equipment-customer-search"
                    clearOnSelect
                    assignField
                    disabled={equipment?.chassis_owner_type !== 'customer'}
                    onChange={(value) => {
                      onChange(equipment, 'chassis_owner', value);
                    }}
                    onSelect={(value, chassis_owner) => {
                      onChange(equipment, 'chassis_owner', chassis_owner);
                    }}
                    value={equipment?.chassis_owner?.name}
                    placeholder={t('SEARCH_FOR_CUSTOMER')}
                    type="customer"
                    data-testid="equipment-customer-search"
                  />
                  {equipment?._chassisOwner === false && (<ErrorLabel> {t('REQUIRED_FIELD')} </ErrorLabel>)}
                </fieldset>
              </div>
            </div>
            <div className="w-1/5 h-10 flex">
              <div className="w-2/3">
                <button
                  className="button button-green"
                  onClick={() => onApplyAll(equipment)}
                  data-testid="apply-equipment-button"
                >
                  {titleize(t('APPLY'))}
                </button>
              </div>
              <div className="w-1/3">
                <button
                  className="button"
                  onClick={() => onRemoveEquipment(equipment)}
                  data-testid="remove-equipment-button"
                >
                  <Glyphicon glyph="remove" className="text-grey-dark" />
                </button>
              </div>
            </div>
          </div>
        );
      })}
      <div>
        <button
          id="order-add-equipment-button"
          className="button button-green mt-4"
          onClick={onAdd}
          data-testid="order-add-equipment-button"
        >
          + {t('ADD_EQUIPMENT')}
        </button>
      </div>
    </div>
  );
};

export default OrderEquipments;
